import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { isMp3 } from "@/lib/music/tagWriter";

const FIELDS = [["title", "Title"], ["artist", "Artist"], ["album", "Album"], ["albumartist", "Album artist"], ["genre", "Genre"], ["year", "Year", true], ["track", "Track #", true], ["disk", "Disc #", true]];
const SHOWN = { song: FIELDS.map((f) => f[0]), many: ["artist", "album", "albumartist", "genre", "year", "disk"], album: ["album", "albumartist", "artist", "genre", "year"] };
const TITLES = { song: "Edit song details", many: "Edit selected songs", album: "Edit album details" };

const common = (list, k) => { const v = list[0]?.[k]; return list.every((t) => (t[k] ?? "") === (v ?? "")) ? String(v ?? "") : null; };

export const EditTagsDialog = ({ m, paths, mode, open, onOpenChange, onSaved }) => {
  const list = paths.map((p) => m.byPath.get(p)).filter(Boolean);
  const keys = SHOWN[mode];
  const [init, setInit] = useState({});
  const [vals, setVals] = useState({});
  const [toFile, setToFile] = useState(true);
  const [busy, setBusy] = useState(false);
  const mp3 = paths.filter(isMp3).length;
  useEffect(() => {
    if (!open) return;
    const i = Object.fromEntries(keys.map((k) => [k, common(list, k)]));
    setInit(i); setVals(Object.fromEntries(keys.map((k) => [k, i[k] ?? ""])));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const isNum = (k) => FIELDS.find((f) => f[0] === k)[2];
  const cleared = (k) => k !== "title" && !vals[k]?.trim() && init[k];
  const changed = Object.fromEntries(keys.filter((k) => cleared(k) || (vals[k]?.trim() && vals[k].trim() !== (init[k] ?? ""))).map((k) => {
    if (cleared(k)) return [k, null];
    return [k, isNum(k) ? Number(vals[k].trim()) : vals[k].trim().slice(0, 200)];
  }));
  const bad = keys.some((k) => k in changed && changed[k] !== null && isNum(k) && (!/^\d{1,4}$/.test(vals[k].trim()) || changed[k] < 1));
  const save = async () => {
    setBusy(true);
    const r = await m.saveTags(paths, changed, toFile && m.canWrite);
    setBusy(false);
    const n = `${r.saved.toLocaleString()} ${r.saved === 1 ? "song" : "songs"}`;
    if (r.written && !r.failed) toast.success(`Updated ${n} and saved into ${r.written === 1 ? "the file" : `${r.written} files`}`);
    else if (r.written || (r.failed && toFile)) toast.warning(`Updated ${n}. ${r.failed} ${r.failed === 1 ? "file" : "files"} couldn't be changed, so those edits live in LoreRight only.`);
    else toast.success(`Updated ${n}`);
    onSaved?.(changed);
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="tag-editor">
        <DialogHeader>
          <DialogTitle>{TITLES[mode]}</DialogTitle>
          <DialogDescription>{list.length === 1 ? `${list[0].path.split("/").pop()}. Empty a field to remove it.` : `${list.length.toLocaleString()} songs. Fields marked Mixed stay as they are unless you type a new value; empty a shared field to remove it.`}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {keys.map((k) => { const [, label, num] = FIELDS.find((f) => f[0] === k); return (
            <label key={k} className={`space-y-1 ${num ? "" : "col-span-2 sm:col-span-1"}`}>
              <span className="text-xs text-[#A8A090]">{label}</span>
              <Input value={vals[k] ?? ""} onChange={(e) => setVals((v) => ({ ...v, [k]: e.target.value }))} inputMode={num ? "numeric" : undefined} placeholder={init[k] === null ? "Mixed" : ""} className="h-8 text-sm" data-testid={`tag-field-${k}`} />
            </label>
          ); })}
        </div>
        {mp3 > 0 && m.canWrite && (
          <label className="flex items-start gap-2 rounded border border-[#2D3442] p-2 text-xs text-[#D6CCBC]">
            <Checkbox checked={toFile} onCheckedChange={(v) => setToFile(!!v)} className="mt-0.5" data-testid="tag-write-file" />
            <span>Also save into {mp3 === 1 ? "the MP3 file" : `${mp3.toLocaleString()} MP3 files`}<span className="block text-[11px] text-[#7A7468]">Your browser will ask to allow changes. Cover art, lyrics and comments are kept; rarely used tag fields may be dropped.{mp3 < paths.length ? " Other formats are saved in LoreRight only." : ""}</span></span>
          </label>
        )}
        {!(mp3 > 0 && m.canWrite) && <p className="text-[11px] text-[#7A7468]" data-testid="tag-app-only-note">Changes are saved in LoreRight; your files stay as they are.{mp3 > 0 ? " To save into MP3 files, connect your folder in Chrome or Edge." : ""}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="tag-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy || bad || !Object.keys(changed).length} data-testid="tag-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
        {bad && <p className="text-[11px] text-[#F87171]" data-testid="tag-number-error">Year, track and disc must be whole numbers from 1 to 9999.</p>}
      </DialogContent>
    </Dialog>
  );
};
